import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import usePlanMatrix from "@/hooks/usePlanMatrix";
import { canUseFeature } from "@/lib/feature-access";
import { getFeatureMeta } from "@/lib/feature-metadata";
import { useI18n } from "@/lib/i18n";
import { BrandSpinner } from "@/components/shared/ContentSpinner";
import { buildStudentPath } from "@/lib/academy-auth-url";

/**
 * ModuleLockGuard — wraps a route element. If the academy's current plan
 * doesn't include `featureKey`, shows a toast and redirects to the
 * appropriate dashboard. Use for entire Pro-only modules (e.g. Certificates,
 * Sessions, Question Bank).
 *
 * Modes:
 *   - "instructor" (default): uses useInstructorAuth, redirects to
 *     /instructor/dashboard, toast includes "Upgrade" CTA → billing.
 *   - "student": reads `academy` from outlet context (AcademySlugShell),
 *     redirects to /:slug/dashboard, no upgrade CTA (student can't upgrade).
 */
export default function ModuleLockGuard({
  featureKey,
  children,
  mode = "instructor",
  redirectTo,
}) {
  const isStudent = mode === "student";
  // Read academy from the outlet context (provided by InstructorLayout /
  // AcademySlugShell). Both layouts already wait for academy to load
  // before mounting nested routes, so we should never see white screens.
  const outletCtx = useOutletContext();
  const params = useParams();
  const { matrix } = usePlanMatrix();
  const { lang } = useI18n();
  const navigate = useNavigate();
  const toastShownRef = useRef(false);
  const [fallbackRedirect, setFallbackRedirect] = useState(false);

  const slug = params?.slug || outletCtx?.academy?.slug;
  const academy = outletCtx?.academy ?? null;
  // For student-mode (public academy routes) we treat unauthenticated
  // visitors specially: never bounce them to a dashboard URL that would
  // demand login. Render an inline "feature unavailable" notice instead.
  const isUnauthGuest =
    isStudent && outletCtx?.isAuthenticated === false;
  const target =
    redirectTo ||
    (isStudent
      ? buildStudentPath(slug || "", "dashboard")
      : "/instructor/dashboard");

  const ready = !!academy && !!matrix;
  const allowed = ready ? canUseFeature(featureKey, academy, matrix) : null;

  useEffect(() => {
    if (toastShownRef.current) return;
    if (!ready || allowed) return;
    // Don't toast unauthenticated public visitors — they get an inline UI.
    if (isUnauthGuest) return;
    toastShownRef.current = true;
    const meta = getFeatureMeta(featureKey);
    const label = meta ? (lang === "en" ? meta.label_en : meta.label_ar) : "";
    const title = isStudent
      ? lang === "en"
        ? `${label} is unavailable`
        : `${label} غير متاحة في هذه الأكاديمية`
      : lang === "en"
        ? `${label} requires Pro`
        : `${label} تتطلب باقة Pro`;
    const description = isStudent
      ? lang === "en"
        ? "This feature is not enabled by your academy. Redirected to dashboard."
        : "هذه الميزة غير مفعّلة في أكاديميتك. تم تحويلك إلى لوحة التحكم."
      : lang === "en"
        ? "This feature is only available on the Pro plan. Redirected to dashboard."
        : "هذه الميزة متاحة في باقة Pro فقط. تم تحويلك إلى لوحة التحكم.";
    toast(title, {
      description,
      duration: 6000,
      ...(isStudent
        ? {}
        : {
            action: {
              label: lang === "en" ? "Upgrade" : "ترقية",
              onClick: () =>
                navigate(
                  `/instructor/settings/billing?from=${featureKey}&section=subscription`
                ),
            },
          }),
    });
  }, [ready, allowed, featureKey, lang, navigate, isStudent]);

  // Defensive fallback: if we're stuck waiting (no academy in context, or
  // matrix never resolves) for more than 600ms, redirect to dashboard
  // instead of showing an infinite spinner.
  useEffect(() => {
    if (ready) return;
    const t = setTimeout(() => setFallbackRedirect(true), 600);
    return () => clearTimeout(t);
  }, [ready]);

  if (!ready) {
    if (fallbackRedirect) {
      return <Navigate to={target} replace />;
    }
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <BrandSpinner size="sm" />
      </div>
    );
  }

  if (!allowed) {
    if (isUnauthGuest) {
      return (
        <div className="text-center py-24 px-6">
          <p className="text-sm text-muted-foreground">
            {lang === "en"
              ? "This feature is not available right now."
              : "هذه الميزة غير متاحة حالياً."}
          </p>
        </div>
      );
    }
    return <Navigate to={target} replace />;
  }

  return children;
}
